import dayjs from "dayjs";
import { preferTrainTrips } from "../../../Lib/Utils/booking";
import { postBooking } from "../../../app/api/postBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { uuid } from "../../../core/utils/Uuid";
import { getPassengerAge, isActionRunSuccessfully, shouldSharePlaceWithGuardian } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState, updateRecoilStateWithType } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IBookedOffer, IBookedTrips, IBookingTypeTrip, IDeterminePassenger, IExchangeOfferRequest, IQuickExchangeOfferRequest, OffersRequest, PassengerSpecification, TrainOfferRequest } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IGetAllTripWithoutExchangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { IBookingRequest, IBookingResponse } from "../../../Orchestrator/Types/Types/BookingTypes";
import { getPriceCategoryOfferPassengerRefs } from "../../../Orchestrator/Types/Types/TrainTypes";
import { showNotification } from "../../../Services/notificationService";
import { fetchExchangeOffersAction } from "../ChangeDate/FetchExchangeOffersAction";
import { startPreExchangeOperationAction } from "../ChangeDate/StartPreExchangeOperationAction";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { getSearchResultAction } from "../Search/GetSearchResultAction";
import { enumPassType, TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getFmlmViewInformationAction } from "../../UIActions/FmlmViewInformation/GetFmlmViewInformationAction";
import { DEFAULT_FMLM_FILTER_STATE } from "../../../Components/TrainOffer/FmlmTrainOffer/FmlmFilterDrawer";
import { addReturnJourneyToBookingAction } from "../BookReturnJourney/AddReturnJourneyToBookingAction";
import { createOrUpdateBookingCommentAction } from "../BookingDetail/CreateOrUpdateBookingCommentAction";
import { ICreateOrUpdateBookingActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/CreateOrUpdateBookingCommentActionParams";
import { IAppliedPassengerPassAtomType } from "../../../Orchestrator/Recoils/BookingAtom";

export class GetTrainOfferContinueAction extends BaseAction<{ returnText: string; checkoutRedirect: boolean; }, TrainOfferRequest> {

    protected override deduplicate = true;

    async processBooking(offerIds: string[] | any, offersRequest: OffersRequest | any, isAnonymous: boolean, callBack?: (status: string) => void): Promise<{ booking: IBookingResponse } | null> {
        // Prepare the booking parameters        
        const passengerRefs: string[] = [];
        const passengerSpecifications: PassengerSpecification[] = [];

        offersRequest.anonymousPassengerSpecifications.forEach((element: any) => {
            passengerRefs.push(element.externalRef);

            const appliedPassengerPassAtom = getRecoilStateValue<IAppliedPassengerPassAtomType>("appliedPassengerPassAtom");

            const outboundPassCards = appliedPassengerPassAtom?.outbound?.filter(pass => pass.externalRef === element.externalRef) || []
            const inboundPassCards = appliedPassengerPassAtom?.inbound?.filter(pass => pass.externalRef === element.externalRef) || []

            const matchPass = [outboundPassCards, inboundPassCards].flat().filter(pass => pass.externalRef === element.externalRef)
            const processedPassCards = [
                ...(element?.cards?.filter((i: any) => i?.type !== enumPassType.TRAVEL_PASS) || []),
                ...(matchPass.map(pass => ({
                    "code": pass.passOffer?.booking?.booking?.bookedOffers?.[0]?.products?.[0]?.code,
                    "number": pass.passOffer.travelAccount.travelAccount.number,
                    "useConcessionVouchers": false,
                    "issuer": pass.passOffer.travelAccount.travelAccount.issuer,
                    "type": enumPassType.TRAVEL_PASS
                })) || []),
            ];

            passengerSpecifications.push({
                externalRef: element.externalRef,
                detail: matchPass ? {
                    firstName: matchPass[0]?.passOffer?.travelAccount?.travelAccount?.holder?.firstName || "",
                    lastName: matchPass[0]?.passOffer?.travelAccount?.travelAccount?.holder?.lastName || "",
                    email: element.detail?.email || "",
                    age: element.age || 0
                } : null,
                type: element.type,
                age: element.age,
                cards: processedPassCards,
                ...(element.prmNeeds?.length > 0 ? { prmNeeds: element.prmNeeds } : {})
            });
        });
        const bookingParams = {
            offers: offerIds.map((offerId: any) => ({
                offerId: offerId,
                passengerRefs: passengerRefs,
            })),
            passengerSpecifications: passengerSpecifications,
            externalRef: uuid(),
        };
        // Construct the IBookingRequest object from the prepared bookingParams
        const newBookingRequest: IBookingRequest = {
            offers: bookingParams.offers,
            passengerSpecifications: bookingParams.passengerSpecifications,
            externalRef: bookingParams.externalRef,
        };

        // Update the booking request state in Recoil
        updateRecoilState<IBookingRequest>('bookingRequestAtom', newBookingRequest);

        try {
            // Clear the booking response atom before making the request
            updateRecoilState('bookingResponseAtom', null);
            // NB: shuttleAddedAtom / nolAddedAtom are deliberately NOT reset here.
            // Six call sites create bookings; resetting at one of them reads as
            // coverage the other five don't have. The carried-entry leak is fixed
            // at the point that consumes it — confirmOnwardSelection drops an
            // entry absent from the current booking — plus BOOKING_FLOW_ATOM_KEYS
            // for flow entry. See clearBookingFlowAtoms.ts.
            // Post booking data to the API
            const response: IApiResponse<IBookingResponse> = await postBooking(newBookingRequest);
            if (response.status === 'ok' && response.answer) {

                const bookingResponse: IBookingResponse = response.answer;
                // Update Recoil state with the booking response                

                updateRecoilState('bookingResponseAtom', bookingResponse);

                prepareBookingSummaryAction.setParams(bookingResponse.booking)
                await prepareBookingSummaryAction.execute()

                if (isAnonymous) {
                    updateRecoilState("guestCheckoutBookingIdAtom", bookingResponse.booking.id)
                }

                if (callBack) {
                    callBack("success")
                }
                return {
                    booking: bookingResponse
                }
            }
        } catch (error: any) {
            console.error('Error processing booking:', error);
            return null
        }
        // Ensure a return statement for all code paths
        return null;
    }


    override async run(): Promise<{ returnText: string; checkoutRedirect: boolean; }> {
        let returnText: string = "success"
        let checkoutRedirect: boolean = false;
        try {
            const { search, offerId, isAnonymous, totalPassengerCount, passengerCounts, currentTrainType,
                outboundOfferId, offersRequest, selectedOutboundTrain, selectedInboundTrain, inboundSearchState,
                passengerSpecificationsTypes, shuttleOfferId, outboundShuttleOfferId, isChangeDate, booking,
                selectedPassengerForCancel, selectedPassengerForCancelInBound, inboundSelectedChangeDateAtom, selectedChangeDateAtom,
                SelectedOfferIdChangeDateAtom, searchStates, selectedTrain, changeTripAlreadyPaidAddons, fmlmFilter, isBookReturnJourney } = this.params!;

            const normalizeIds = (value: any): string[] => {
                if (Array.isArray(value)) {
                    return value.filter((v) => typeof v === "string" && v.trim().length > 0);
                }
                if (typeof value === "string" && value.trim().length > 0) {
                    return [value];
                }
                return [];
            };

            const offerIdList = normalizeIds(offerId);
            const outboundOfferIdList = normalizeIds(outboundOfferId);
            const shuttleOfferIdList = normalizeIds(shuttleOfferId);
            const outboundShuttleOfferIdList = normalizeIds(outboundShuttleOfferId);

            if (offerIdList.length === 0) {
                showNotification("warning", "Please select a train", "Select a train and price class before continuing.");
                returnText = "warning";
            }
            // if (isAnonymous) {
            //     // For outbound journey of a round trip
            //     if (search.outboundDate && search.inboundDate && currentTrainType === "outbound") {
            //         // Store outbound selection and continue to inbound selection
            //         updateRecoilState("outboundOfferIdAtom", offerId);
            //         updateRecoilState("outboundShuttleOfferIdAtom", shuttleOfferId);
            //         updateRecoilState("trainTypeAtom", "inbound");
            //         const searchParams = {
            //             fromName: search.toName,
            //             toName: search.fromName,
            //             departureTime: search.inboundDate.format("YYYY-MM-DDTHH:mm:ss"),
            //             arrivalTime: search.outboundDate?.format("YYYY-MM-DDTHH:mm:ss"),
            //             origin: search.to,
            //             destination: search.from,
            //             totalPassengerCount: totalPassengerCount,
            //             passengerCounts: passengerCounts,
            //             trainType: "inbound",
            //         };
            //         updateRecoilState("inboundSearchStateAtom", searchParams);
            //         //offerService.processSearch(searchParams);
            //         getSearchResultAction.setParams(searchParams);
            //         getSearchResultAction.execute();
            //         returnText = "success";
            //     } else {
            //         // Redirect to login only after both selections are complete
            //         updateRecoilState("inboundOfferIdAtom", offerId);
            //         // const selectedDate = search.outboundDate;
            //         // const offerIds =
            //         //     search.outboundDate && search.inboundDate ?
            //         //         [outboundOfferId, offerId, ...(outboundShuttleOfferId ? [outboundShuttleOfferId] : []), ...(shuttleOfferId ? [shuttleOfferId] : [])]
            //         //         : (shuttleOfferId) ? [offerId, shuttleOfferId] : [offerId];

            //         // updateRecoilState("pendingBookingDataAtom", {
            //         //     outboundOfferId:
            //         //         currentTrainType === "outbound" ? offerId : outboundOfferId,
            //         //     inboundOfferId: currentTrainType === "inbound" ? offerId : null,
            //         //     offersRequest,
            //         //     selectedOutboundTrain,
            //         //     selectedInboundTrain,
            //         //     search,
            //         //     inboundSearchState,
            //         //     selectedDate,
            //         //     passengers: passengerCounts,
            //         //     offerIds: offerIds
            //         // })

            //         // sessionStorage.setItem("redirectAfterLogin", "/checkout");
            //         // sessionStorage.setItem("redirectAfterLoginFlag", "true");
            //         // sessionStorage.setItem(
            //         //     "pendingBookingData",
            //         //     JSON.stringify({
            //         //         outboundOfferId:
            //         //             currentTrainType === "outbound" ? offerId : outboundOfferId,
            //         //         inboundOfferId: currentTrainType === "inbound" ? offerId : null,
            //         //         offersRequest,
            //         //         selectedOutboundTrain,
            //         //         selectedInboundTrain,
            //         //         search,
            //         //         inboundSearchState,
            //         //         selectedDate,
            //         //         passengers: passengerCounts,
            //         //         offerIds: offerIds
            //         //     })
            //         // );
            //         // returnText = "needLogin";
            //         returnText = "success";
            //     }
            // } else {
            //     returnText = "success"
            // }

            if (returnText === "success") {
                if (isChangeDate == true) {
                    //Change Date Operation
                    if (booking && (selectedPassengerForCancel || selectedPassengerForCancelInBound)) {
                        if (inboundSelectedChangeDateAtom && currentTrainType === TrainTypeEnum.outbound) {
                            //#region Change Date Operation for Inbound Journey
                            updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
                            //updateRecoilState("dateStateAtom", endDate)
                            const anonymousPassengerSpecifications =
                                booking?.booking?.passengers
                                    .filter(
                                        (p) =>
                                            selectedPassengerForCancel!.includes(p.id) ||
                                            selectedPassengerForCancelInBound!.includes(p.id)
                                    )
                                    .map((passenger) => ({
                                        id: passenger.id,
                                        type: passenger.type,
                                        externalRef: passenger.externalRef,
                                        age: passenger.age,
                                        cards: passenger.cards || [],
                                        prmNeeds: passenger.prmNeeds || [],
                                        sharePlaceWithGuardian: shouldSharePlaceWithGuardian(passenger) ? true : undefined
                                    })) || [];


                            await getAllOfferWithoutExchangeAction.setParams(booking?.booking?.bookedOffers)
                            await getAllOfferWithoutExchangeAction.execute()
                            let bookingOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!


                            const params: IGetAllTripWithoutExchangeActionParams = {
                                bookedOffers: bookingOfferWithoutExchange,
                                trips: booking?.booking?.trips
                            }
                            await getAllTripWithoutExchangeAction.setParams(params)
                            await getAllTripWithoutExchangeAction.execute()
                            const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!


                            const inboundBookedOfferOnly = bookingOfferWithoutExchange.filter(offer => preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.IN_BOUND)?.id === offer.tripCoverage.coveredTripId)
                            // ERM infant–adult linking: the backend requires the WHOLE
                            // linked group in the exchange request (400 PASSENGERS_MISSING_CHILDREN
                            // otherwise), so lap-infant admissions are included with their adult.
                            let admissions = inboundBookedOfferOnly?.flatMap(
                                (offer) =>
                                    offer?.admissions?.filter((admission) =>
                                        admission.status === "FULFILLED" &&
                                        admission.passengerIds.some((pid) =>
                                            anonymousPassengerSpecifications.some((p) => p.id === pid)
                                        )
                                    ) || []
                            );

                            const fulfillmentIds: string[] = [];

                            admissions?.forEach((element) => {
                                fulfillmentIds.push(element.id);
                            });

                            // Calculate original price of items being exchanged
                            let originalPriceOfExchangedItemsOutbound = 0;
                            if (booking?.booking?.bookedOffers && fulfillmentIds.length > 0) {
                                booking.booking.bookedOffers.forEach(offer => {
                                    offer.admissions?.forEach(admission => {
                                        if (fulfillmentIds.includes(admission.id)) {
                                            originalPriceOfExchangedItemsOutbound += admission.price.amount;
                                        }
                                    });
                                });
                            }

                            if (originalPriceOfExchangedItemsOutbound > 0) {
                                const scale = booking.booking.bookedOffers[0]?.admissions[0]?.price?.scale;
                                updateRecoilState("originalPriceOfExchangedItemsInboundAtom", null);
                                updateRecoilState("originalPriceOfExchangedItemsInboundAtom", (originalPriceOfExchangedItemsOutbound / ((100 * scale) / scale)));
                            }
                            const fallbackInboundTrip = preferTrainTrips(booking?.booking?.trips).find(t => t.direction === TripTypeEnum.IN_BOUND);
                            const exchangeOffersRequest: IQuickExchangeOfferRequest = {
                                fulfillmentIds,
                                overruleCode: "",
                                tripSearchCriteria: {
                                    departureTime: inboundSelectedChangeDateAtom
                                        ? dayjs(new Date(inboundSelectedChangeDateAtom)).format("YYYY-MM-DD") + "T00:00:00" : "",
                                    origin: {
                                        objectType: "StopPlaceRef",
                                        stopPlaceRef: fallbackInboundTrip?.origin.stopPlaceRef || "",
                                    },
                                    destination: {
                                        objectType: "StopPlaceRef",
                                        stopPlaceRef: fallbackInboundTrip?.destination.stopPlaceRef || "",
                                    },
                                    direction: TripTypeEnum.IN_BOUND,
                                    // returnSearchParameters: (inboundSelectedChangeDateAtom && selectedChangeDateAtom) ?
                                    //     { outwardTripDate: dayjs(selectedChangeDateAtom).format("YYYY-MM-DD[T]00:00:00.000[Z]") } : null
                                },
                                offerSearchCriteria: {
                                    currency: "AED",
                                },
                                anonymousPassengerSpecifications:
                                    anonymousPassengerSpecifications.map((passenger) => {
                                        // const pass: IDeterminePassenger = {
                                        //     age: passenger.age || 0,
                                        //     cards: passenger?.cards || [],
                                        //     prmNeeds: ((passenger?.prmNeeds || []).length > 0) ? passenger?.prmNeeds[0] : ""
                                        // }
                                        // const passengerAge = getPassengerAge(pass)
                                        return {
                                            externalRef: passenger.externalRef,
                                            type: passenger.type,
                                            cards: passenger?.cards || [],
                                            prmNeeds: passenger.prmNeeds || [],
                                            age: passenger?.age,
                                            sharePlaceWithGuardian: passenger.sharePlaceWithGuardian
                                        }
                                    }),
                            };

                            try {
                                await fetchExchangeOffersAction.setParams(
                                    {
                                        booking_id: booking?.booking.id,
                                        exchangeOffersRequest: exchangeOffersRequest,
                                        booking: booking,
                                        originalPriceOfExchangedItems: originalPriceOfExchangedItemsOutbound,
                                        trainType: TrainTypeEnum.inbound,
                                        selectedDate: selectedChangeDateAtom!,
                                        inboundSelectedDate: inboundSelectedChangeDateAtom!
                                    }
                                )
                                await fetchExchangeOffersAction.execute();

                                updateRecoilState("SelectedOfferIdChangeDateAtom", offerId);
                            } catch (error) {
                                console.error("Error fetching exchange offers:", error);
                            }
                            //#endregion
                        }

                        else {
                            //#region Change Date Operation for Oubound Journey


                            let selectedPassengers;
                            //selectedChangeDateAtom && inboundSelectedChangeDateAtom
                            // ? [offerId, SelectedOfferIdChangeDateAtom]
                            // : [offerId];
                            const offerIdsChangeDate = [offerId];
                            if (inboundSelectedChangeDateAtom && SelectedOfferIdChangeDateAtom) {
                                offerIdsChangeDate.push(SelectedOfferIdChangeDateAtom);
                            }

                            // If booking has only one passenger, use it directly
                            if (booking?.booking?.passengers?.length === 1) {
                                selectedPassengers = booking.booking.passengers.map((passenger) => ({
                                    ...passenger,
                                    detail: {
                                        ...passenger.detail,
                                        firstName: passenger.detail?.firstName || "",
                                        lastName: passenger.detail?.lastName || "",
                                        email: passenger.detail?.email || "",
                                    },
                                }));
                            } else {
                                selectedPassengers =
                                    booking?.booking?.passengers
                                        .filter(
                                            (p) =>
                                                selectedPassengerForCancel!.includes(p.id) ||
                                                selectedPassengerForCancelInBound!.includes(p.id)
                                        )
                                        .map((passenger) => ({
                                            ...passenger,
                                            detail: {
                                                ...passenger.detail,
                                                firstName: passenger.detail?.firstName || "",
                                                lastName: passenger.detail?.lastName || "",
                                                email: passenger.detail?.email || "",
                                            },
                                        })) || [];
                            }

                            const bookedTrip: IBookedTrips = {
                                bookingId: booking?.booking?.id || "",
                                fromName: searchStates?.fromName || "",
                                toName: searchStates?.toName || "",
                                from: searchStates?.from || "",
                                to: searchStates?.to || "",
                                departureTime: selectedTrain?.departureTime || "",
                                arrivalTime: selectedTrain?.arrivalTime || "",
                                class: selectedTrain?.selectedPriceClass || "",
                                summary:
                                    selectedTrain?.selectedPriceClass === "premium"
                                        ? "Premium"
                                        : "Standard Value",
                                type: "Adult",
                                passengers: selectedPassengers,
                                duration: selectedTrain?.duration || "",
                                trainNumber: booking?.booking?.bookingCode || "",
                                status: "FULFILLED",
                                startDate: selectedChangeDateAtom!,
                                ticketLink:
                                    booking?.booking?.fulfillments?.[0]?.fulfillmentDocuments?.[0]
                                        ?.downloadLink || "",
                                arrivalDateTime: selectedTrain?.arrivalTime || "",
                                departureDateTime: selectedTrain?.departureTime || "",
                                bookingType: "oneWayTrip",
                                stops: [],
                                inboundData: null,
                            };

                            // Extract per-offer-passenger mapping from selected price class
                            const currentOfferPassengerRefs = getPriceCategoryOfferPassengerRefs(
                                selectedTrain?.priceInfo,
                                selectedTrain?.selectedPriceClass
                            );

                            //pre exchange operation start change date (to show the additional offers api response)
                            await startPreExchangeOperationAction.setParams({
                                exchangeOfferId: offerIdsChangeDate?.filter((id): id is string => id !== undefined) ?? [],
                                exchangeOfferPassengerRefs: currentOfferPassengerRefs.length > 0 ? currentOfferPassengerRefs : undefined,
                                nextTrainTicket: bookedTrip,
                                selectedPassengerForCancel: selectedPassengerForCancel!,
                                selectedPassengerForCancelInBound: selectedPassengerForCancelInBound!,
                                changeTripAlreadyPaidAddons: changeTripAlreadyPaidAddons
                            });
                            await startPreExchangeOperationAction.execute();

                            if (isActionRunSuccessfully(startPreExchangeOperationAction)) {
                                const fetchResult: IDefaultActionResult = startPreExchangeOperationAction.result!
                                if (fetchResult.status == "success") {
                                    checkoutRedirect = true;
                                }
                            }
                            //#endregion
                        }
                    }
                }
                else {
                    //Normal Booking Operation
                    if (search.outboundDate && search.inboundDate && currentTrainType === TrainTypeEnum.outbound) {

                        let isFmlmBooking = search.fromfmlmSearch || search.tofmlmSearch

                        if (isFmlmBooking) {
                            updateRecoilState("outboundOfferIdAtom", offerId);
                            updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
                            getFmlmViewInformationAction.setParams({
                                search: search,
                                fmlmFilter: fmlmFilter || DEFAULT_FMLM_FILTER_STATE,
                                isTimerVisible: false,
                                isFromPopUp: false,
                                trainType: TrainTypeEnum.inbound
                            });
                            await getFmlmViewInformationAction.execute();

                            const searchParams = {
                                fromName: search.toName,
                                toName: search.fromName,
                                departureTime: search.inboundDate.format("YYYY-MM-DDTHH:mm:ss"),
                                arrivalTime: search.outboundDate.format("YYYY-MM-DDTHH:mm:ss"),
                                origin: search.to,
                                destination: search.from,
                                totalPassengerCount: totalPassengerCount,
                                passengerCounts: passengerCounts,
                                trainType: TrainTypeEnum.inbound,
                                selectedTrain: selectedOutboundTrain,
                            };
                            updateRecoilState("inboundSearchStateAtom", searchParams);
                        } else {
                            //#region Process Booking
                            updateRecoilState("outboundOfferIdAtom", offerId);
                            updateRecoilState("outboundShuttleOfferIdAtom", shuttleOfferId);
                            updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
                            const searchParams = {
                                fromName: search.toName,
                                toName: search.fromName,
                                departureTime: search.inboundDate.format("YYYY-MM-DDTHH:mm:ss"),
                                arrivalTime: search.outboundDate.format("YYYY-MM-DDTHH:mm:ss"),
                                origin: search.to,
                                destination: search.from,
                                totalPassengerCount: totalPassengerCount,
                                passengerCounts: passengerCounts,
                                trainType: TrainTypeEnum.inbound,
                                selectedTrain: selectedOutboundTrain,
                            };
                            updateRecoilState("inboundSearchStateAtom", searchParams);
                            //offerService.processSearch(searchParams, selectedTrain);
                            getSearchResultAction.setParams(searchParams);
                            getSearchResultAction.execute();
                            //#endregion
                        }
                    } else {
                        if (!isBookReturnJourney) {
                            updateRecoilState("inboundOfferIdAtom", offerId);
                            //also check if both search.outboundDate && search.inboundDate are there and with Shuttle then need to pass 4 offer Ids (2 for Outbound and 2 for Inbound)
                            let offerIds: string[] = [];
                            if (search.outboundDate && search.inboundDate) {
                                // Round-trip: outbound first, then inbound; include shuttles if present
                                offerIds = [
                                    ...outboundOfferIdList,
                                    ...offerIdList,
                                    ...outboundShuttleOfferIdList,
                                    ...shuttleOfferIdList,
                                ];
                            } else {
                                // One-way: inbound/current selection (offerId) then shuttle if present
                                offerIds = [
                                    ...offerIdList,
                                    ...shuttleOfferIdList,
                                ];
                            }

                            const availableIds = new Set<string>();
                            offerIds = offerIds.filter((id) => {
                                if (!id || availableIds.has(id)) return false;
                                availableIds.add(id);
                                return true;
                            });

                            let processBookingData = await this.processBooking(
                                offerIds,
                                offersRequest,
                                isAnonymous
                            );
                            checkoutRedirect = true;

                            // Fill passenger state for pass bookings
                            const isPassBooking = processBookingData?.booking.booking.passengers.some(passenger => passenger.cards.some(card => card.number));
                            if (isPassBooking && processBookingData) {
                                // Update passenger state with booking passengers for pass bookings
                                const filteredBookingPassengers = processBookingData.booking.booking.passengers
                                updateRecoilState("passengerStateAtom", filteredBookingPassengers);
                            }


                            let isFmlmBooking = search.fromfmlmSearch || search.tofmlmSearch
                            // Save booking comment for FMLM journeys planner inside booking detail
                            if (isFmlmBooking && processBookingData?.booking) {
                                // this code will save comment for start and end location for fmlm
                                let bookingComment: ICreateOrUpdateBookingActionParams = {
                                    bookingId: processBookingData?.booking?.booking?.id || "",
                                    comment: {
                                        journey_planner: [{
                                            direction: TripTypeEnum.OUT_BOUND,
                                            origin: {
                                                display_name: search.fromName,
                                                cityName: "",
                                                lat: search.fromGeoPosition?.latitude || 0,
                                                lon: search.fromGeoPosition?.longitude || 0,
                                                stopCode: search.from
                                            },
                                            destination: {
                                                display_name: search.toName,
                                                cityName: "",
                                                lat: search.toGeoPosition?.latitude || 0,
                                                lon: search.toGeoPosition?.longitude || 0,
                                                stopCode: search.to
                                            }
                                        }]
                                    }
                                }
                                createOrUpdateBookingCommentAction.setParams(bookingComment)
                                await createOrUpdateBookingCommentAction.execute();
                            }
                        }
                        else {
                            const selectedPassengersForBookReturnJourney =
                                booking?.booking?.passengers
                                    .filter(
                                        (p) =>
                                            selectedPassengerForCancel!.includes(p.id) ||
                                            selectedPassengerForCancelInBound!.includes(p.id)
                                    ) || [];
                            addReturnJourneyToBookingAction.setParams({
                                offerId: offerId,
                                bookingId: booking?.booking?.id || "",
                                passengers: selectedPassengersForBookReturnJourney || []
                            });
                            await addReturnJourneyToBookingAction.execute();
                            checkoutRedirect = true;
                        }

                    }
                }
            }

        } catch (error) {
            console.error("Error during sign in:", error);
        } finally {
            return { returnText, checkoutRedirect }
        }
    }
}

// Create a singleton instance of the action
export const getTrainOfferContinueAction = new GetTrainOfferContinueAction();
